import React, { useMemo, useState } from 'react';
import type {
	Business,
	BusinessQuery,
	ContactType,
	List,
	UserNotes
} from '@/modules/dashboard/dashboardTypes';

import { sendAnalytics } from '@/services/analyticsService';
import { XIcon } from '@/resources/assets/images';
import { Tabs } from '@/components/atoms/Tabs';
import type { DirectEmail } from '@/modules/email/emailTypes';
import type { User } from '@/modules/user/userTypes';
import { ComposeEmailModal } from '@/components/molecules/ComposeEmailModal';
import type { EmailGetEmailThreadProps } from '@/modules/email/emailActions';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { dashboardSetQuery } from '@/modules/dashboard/dashboardActions';
import type { RootState } from '@/rootReducer';
import {
	ReminderModal,
	ReminderModalProps
} from './src/components/atoms/ReminderModal';
import {
	AddContactDataModal,
	AddContactDataModalProps
} from './src/components/atoms/AddContactDataModal';
import {
	RemoveContactDataModal,
	type RemoveContactDataModalProps
} from './src/components/atoms/RemoveContactDataModal';
import {
	RemoveNoteModal,
	type RemoveNoteModalProps
} from './src/components/atoms/RemoveNoteModal';
import {
	ReportIncorrectDataModal,
	type ReportIncorrectDataModalProps
} from './src/components/atoms/ReportIncorrectDataModal';
import { BusinessInfoTab } from './src/components/molecules/BusinessInfoTab';
import { BusinessEmailsTab } from './src/components/molecules/BusinessEmailsTab';
import { BusinessActivityTab } from './src/components/molecules/BusinessActivityTab';

type BusinessPreviewProps = Pick<
	ReportIncorrectDataModalProps,
	| 'onReportIncorrectData'
	| 'isReportIncorrectDataSuccess'
	| 'isReportIncorrectDataError'
	| 'isReportIncorrectDataLoading'
> &
	Pick<RemoveNoteModalProps, 'onRemoveNote'> &
	Pick<RemoveContactDataModalProps, 'onRemoveContactData'> &
	Pick<
		ReminderModalProps,
		| 'isTaskLoading'
		| 'isTaskError'
		| 'isTaskSuccess'
		| 'onDeleteReminder'
		| 'onResetReminder'
		| 'onSetReminder'
	> &
	Pick<
		AddContactDataModalProps,
		| 'onAddContactData'
		| 'isContactDataError'
		| 'isContactDataLoading'
		| 'isContactDataSuccess'
	> & {
		listId?: List['id'];
		user: User;
		selectedBusiness: Business | null;
		onSaveNote: (
			businessId: string,
			businessName: string,
			notes: UserNotes
		) => void;
		onSelectBusiness: (business: Business | null) => void;
		onGetEmailThread: ({
			businessId,
			userEmail
		}: EmailGetEmailThreadProps) => void;
		lastBusinessIdRef?: React.MutableRefObject<string | null>;
	};

export const BusinessPreview = ({
	listId,
	user,
	selectedBusiness,
	onReportIncorrectData,
	isReportIncorrectDataSuccess,
	isReportIncorrectDataError,
	isReportIncorrectDataLoading,
	onSaveNote,
	onSelectBusiness,
	onGetEmailThread,
	onAddContactData,
	isContactDataError,
	isContactDataLoading,
	isContactDataSuccess,
	isTaskLoading,
	isTaskError,
	isTaskSuccess,
	onDeleteReminder,
	onResetReminder,
	onSetReminder,
	onRemoveNote,
	onRemoveContactData,
	lastBusinessIdRef
}: BusinessPreviewProps) => {
	const [query] = useSelector<RootState, [BusinessQuery]>(
		state => [state.dashboard.get('query').toJS()],
		shallowEqual
	);

	const dispatch = useDispatch();
	const [selectedTab, setSelectedTab] = useState('info');

	const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
	const [customContactDataIndex, setCustomContactDataIndex] = useState<
		number | undefined
	>(undefined);
	const [isReportDataModalOpen, setIsReportDataModalOpen] = useState(false);
	const [isAddDataModalOpen, setIsAddDataModalOpen] = useState(false);
	const [isRemoveDataModalOpen, setIsRemoveDataModalOpen] = useState(false);
	const [directEmail, setDirectEmail] = useState<DirectEmail | undefined>(
		undefined
	);
	const [isComposeEmailModalOpen, setIsComposeEmailModalOpen] =
		useState(false);
	const [isRemoveNoteModalOpen, setIsRemoveNoteModalOpen] = useState(false);

	const setQuery = (q: BusinessQuery) =>
		dispatch(dashboardSetQuery({ ...q, user }));

	const userDataOptions = useMemo(
		() =>
			[
				'',
				selectedBusiness?.email &&
				selectedBusiness?.emailLowPriority !== true
					? selectedBusiness.email
					: undefined,

				selectedBusiness?.facebook?.email &&
				selectedBusiness?.facebook?.emailLowPriority !== true &&
				selectedBusiness?.facebook?.email !== selectedBusiness?.email
					? selectedBusiness.facebook.email
					: undefined,

				selectedBusiness?.web?.email &&
				selectedBusiness?.web?.email !== selectedBusiness?.email &&
				selectedBusiness?.web?.emailLowPriority !== true &&
				selectedBusiness?.web?.email !==
					selectedBusiness?.facebook?.email
					? selectedBusiness.web.email
					: undefined,

				selectedBusiness?.facebook?.messengerUrl
					? 'Facebook Messenger'
					: undefined,

				selectedBusiness?.socials?.instagram
					? 'Instagram Messenger'
					: undefined,

				selectedBusiness?.phone?.phone || undefined
			].filter(
				(item): item is string =>
					typeof item === 'string' && Boolean(item)
			),
		[
			selectedBusiness?.email,
			selectedBusiness?.emailLowPriority,
			selectedBusiness?.facebook?.email,
			selectedBusiness?.facebook?.emailLowPriority,
			selectedBusiness?.facebook?.messengerUrl,
			selectedBusiness?.phone?.phone,
			selectedBusiness?.socials?.instagram,
			selectedBusiness?.web?.email,
			selectedBusiness?.web?.emailLowPriority
		]
	);

	const handleRemoveBusinessSelection = () => {
		onSelectBusiness(null);
	};

	const handleOpenReminderModal = () => {
		setIsReminderModalOpen(true);
	};

	const handleCloseReminderModal = () => {
		setIsReminderModalOpen(false);
	};

	const handleOpenAddDataModal = () => {
		setIsAddDataModalOpen(true);
	};

	const handleCloseAddDataModal = () => {
		setIsAddDataModalOpen(false);
	};

	const handleOpenRemoveDataModal = (index: number) => {
		setIsRemoveDataModalOpen(true);
		setCustomContactDataIndex(index);
	};

	const handleOpenRemoveNoteModal = () => {
		setIsRemoveNoteModalOpen(true);
	};

	const handleCloseRemoveNoteModal = () => {
		setIsRemoveNoteModalOpen(false);
	};

	const handleCloseRemoveDataModal = () => {
		setIsRemoveDataModalOpen(false);
	};

	const handleOpenComposeEmail = (
		business: DirectEmail['business'],
		email: DirectEmail['email']
	) => {
		setDirectEmail({ business, email });

		setIsComposeEmailModalOpen(true);

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User opened a direct email',
			properties: {
				businessName: selectedBusiness?.name,
				businessId: selectedBusiness?._id || selectedBusiness?.id,
				businessAddress: selectedBusiness?.address
			}
		});
	};

	const handleCloseComposeEmailModal = () => {
		setIsComposeEmailModalOpen(false);
	};

	const handleOpenReportDataModal = () => {
		setIsReportDataModalOpen(true);
	};

	const handleCloseReportDataModal = () => {
		setIsReportDataModalOpen(false);
	};

	const handleToggleContactedUserData = (index: number) => {
		const newBusiness = {
			...selectedBusiness,
			userAddedData: selectedBusiness?.userAddedData?.map(
				(element, userIndex) => {
					if (userIndex === index) {
						return { ...element, checked: !element.checked };
					}
					return element;
				}
			)
		};

		let contacted = false;

		if (newBusiness.userAddedData) {
			newBusiness.userAddedData.forEach(data => {
				if (data.checked) {
					contacted = true;
				}
			});
		}

		if (newBusiness.contactType) {
			const contactKeys = Object.keys(newBusiness.contactType) as Array<
				keyof typeof newBusiness.contactType
			>;

			contactKeys.forEach(key => {
				if (newBusiness.contactType && newBusiness.contactType[key]) {
					contacted = true;
				}
			});
		}

		const note = {
			isContacted: contacted,
			lead: newBusiness.lead,
			note: newBusiness.note ?? '',
			contactType: newBusiness.contactType,
			userAddedData: newBusiness.userAddedData ?? ''
		};

		const businessId = newBusiness._id || newBusiness.businessId;

		onSaveNote(
			businessId as string,
			newBusiness?.businessName,
			note as UserNotes
		);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User contacted a business via their User Added Data`,
				properties: {
					address: newBusiness?.address,
					id: newBusiness?._id,
					name: newBusiness?.name
				}
			});
		}
	};

	const handleToggleContacted = (type: string, link = false) => {
		const keyType = type as keyof ContactType;

		let contactType: ContactType = {
			[type]: true
		};

		if (selectedBusiness?.contactType) {
			if (link) {
				contactType = {
					...selectedBusiness?.contactType,
					[keyType]: true
				};
			} else {
				contactType = {
					...selectedBusiness?.contactType,
					[keyType]: !(
						selectedBusiness?.contactType[keyType] ?? false
					)
				};
			}
		}

		let businessIsContacted: boolean | undefined =
			selectedBusiness?.isContacted;

		if (
			[
				'messenger',
				'phone',
				'emailPrimary',
				'emailSecondary',
				'thirdEmail',
				'instagramMessenger'
			].includes(type)
		) {
			const userAddedContact = !!selectedBusiness?.userAddedData?.find(
				data => data.checked
			);

			businessIsContacted = contactType?.[keyType] || userAddedContact;
		}

		const businessId =
			selectedBusiness?.businessId ||
			selectedBusiness?.id ||
			selectedBusiness?._id;

		if (!businessId) return null;

		onSaveNote?.(businessId, selectedBusiness?.name, {
			...selectedBusiness,
			isContacted: businessIsContacted,
			contactType
		} as UserNotes);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User contacted a business via their: ${type.toUpperCase()}`,
				properties: {
					businessName: selectedBusiness?.name,
					businessId: selectedBusiness?._id || selectedBusiness?.id,
					businessAddress: selectedBusiness?.address
				}
			});
		}
	};

	const handleToggleLead = (value: boolean) => {
		if (!onSaveNote) return null;

		const businessId =
			selectedBusiness?.businessId ||
			selectedBusiness?.id ||
			selectedBusiness?._id;

		if (!businessId) return null;

		onSaveNote?.(businessId, selectedBusiness?.name, {
			...selectedBusiness,
			lead: value
		} as UserNotes);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Lead was changed from the business preview',
				properties: {
					address: selectedBusiness?.address,
					id: selectedBusiness?._id,
					name: selectedBusiness?.name
				}
			});

			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName:
					value === undefined
						? 'Lead was set to neutral'
						: `Lead was ${value ? 'added' : 'removed'}`,
				properties: {
					address: selectedBusiness?.address,
					id: selectedBusiness?._id,
					name: selectedBusiness?.name
				}
			});
		}
	};

	const handleEmailThread = (business: Business) => {
		if (user && user.email) {
			const businessId = (business._id ||
				business.businessId ||
				business.id) as string;

			onGetEmailThread({
				businessId,
				userEmail: user.email
			});
		}
	};

	const handleSelectTab = (tabValue: string) => {
		setSelectedTab(tabValue);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `Opened the ${tabValue} tab`,
				properties: {
					businessId:
						selectedBusiness?.businessId || selectedBusiness?._id,
					businessName: selectedBusiness?.name
				}
			});
		}
	};

	const renderEmailsTab = () => {
		if (!selectedBusiness) return;
		if (selectedBusiness._id !== lastBusinessIdRef?.current) {
			if (lastBusinessIdRef) {
				// eslint-disable-next-line no-param-reassign
				lastBusinessIdRef.current = selectedBusiness._id;
			}
			handleEmailThread(selectedBusiness);
		}

		return (
			<BusinessEmailsTab
				business={selectedBusiness}
				openComposeEmail={handleOpenComposeEmail}
				getEmailThread={handleEmailThread}
				onSelectTab={handleSelectTab}
			/>
		);
	};

	if (!selectedBusiness) return null;

	return (
		<>
			<section className="absolute left-0 top-0 h-full w-full overflow-auto">
				<Tabs.Root value={selectedTab} onValueChange={setSelectedTab}>
					<header className="sticky top-0 z-10">
						<section className="flex items-center justify-between bg-dm-black3 p-3 text-dm-white md:p-6">
							<h2 className="text-base font-semibold md:text-lg lg:text-xl 2xl:text-2xl">
								{selectedBusiness.name}
							</h2>

							<button
								type="button"
								onClick={handleRemoveBusinessSelection}
							>
								<XIcon className="[&_path]:fill-dm-white" />
							</button>
						</section>

						<Tabs.List
							className="w-full bg-dm-black3 pt-3 md:pt-6"
							marginLess
						>
							<Tabs.Trigger value="info">Info</Tabs.Trigger>
							<Tabs.Trigger value="emails">Emails</Tabs.Trigger>
							<Tabs.Trigger value="activity">
								Activity
							</Tabs.Trigger>
						</Tabs.List>
					</header>

					<Tabs.Content
						value="info"
						className="relative h-full overflow-hidden"
					>
						<BusinessInfoTab
							selectBusiness={selectedBusiness}
							openSetReminder={handleOpenReminderModal}
							openAddDataModal={handleOpenAddDataModal}
							toggleContactedUserData={
								handleToggleContactedUserData
							}
							toggleContacted={handleToggleContacted}
							openComposeEmail={handleOpenComposeEmail}
							saveNote={onSaveNote}
							openDeleteDataModal={handleOpenRemoveDataModal}
							openReportIncorrectDataModal={
								handleOpenReportDataModal
							}
							toggleLead={handleToggleLead}
						/>
					</Tabs.Content>

					<Tabs.Content value="emails">
						{renderEmailsTab()}
					</Tabs.Content>

					<Tabs.Content value="activity">
						<BusinessActivityTab
							openOnEmail={handleOpenComposeEmail}
							openDeleteNoteModal={handleOpenRemoveNoteModal}
							onSelectTab={handleSelectTab}
						/>
					</Tabs.Content>
				</Tabs.Root>
			</section>

			{customContactDataIndex !== undefined && (
				<RemoveContactDataModal
					userEmail={user.email}
					businessId={selectedBusiness._id}
					listId={listId}
					dataInd={customContactDataIndex}
					isOpen={isRemoveDataModalOpen}
					onCloseModal={handleCloseRemoveDataModal}
					onRemoveContactData={onRemoveContactData}
				/>
			)}

			<RemoveNoteModal
				isOpen={isRemoveNoteModalOpen}
				onCloseModal={handleCloseRemoveNoteModal}
				onRemoveNote={onRemoveNote}
			/>

			<AddContactDataModal
				businessUserAddedData={selectedBusiness.userAddedData}
				businessName={selectedBusiness.name}
				businesslistId={selectedBusiness.listId}
				businessId={selectedBusiness._id}
				userEmail={user.email}
				isOpen={isAddDataModalOpen}
				onCloseModal={handleCloseAddDataModal}
				onAddContactData={onAddContactData}
				isContactDataError={isContactDataError}
				isContactDataLoading={isContactDataLoading}
				isContactDataSuccess={isContactDataSuccess}
			/>

			<ReportIncorrectDataModal
				businessId={selectedBusiness._id}
				isOpen={isReportDataModalOpen}
				onCloseModal={handleCloseReportDataModal}
				userEmail={user.email}
				userDataOptions={userDataOptions}
				onReportIncorrectData={onReportIncorrectData}
				isReportIncorrectDataSuccess={isReportIncorrectDataSuccess}
				isReportIncorrectDataError={isReportIncorrectDataError}
				isReportIncorrectDataLoading={isReportIncorrectDataLoading}
			/>

			<ReminderModal
				isOpen={isReminderModalOpen}
				onCloseModal={handleCloseReminderModal}
				isTaskLoading={isTaskLoading}
				isTaskError={isTaskError}
				isTaskSuccess={isTaskSuccess}
				onDeleteReminder={onDeleteReminder}
				onResetReminder={onResetReminder}
				onSetReminder={onSetReminder}
				hasReminderTaskSet={
					selectedBusiness.reminder?.hasReminderTaskSet
				}
				reminderTriggerDate={
					selectedBusiness.reminder?.reminderTriggerDate
				}
				reminderText={selectedBusiness.reminder?.reminderText}
				businessId={selectedBusiness._id}
				businessName={selectedBusiness.name}
				listId={selectedBusiness.listId}
				originalNote={selectedBusiness.note}
				userEmail={user.email}
				userTimeZone={user.timeZone}
			/>

			<ComposeEmailModal
				isOpen={isComposeEmailModalOpen}
				onCloseModal={handleCloseComposeEmailModal}
				businessSelected={selectedBusiness}
				directEmail={directEmail}
				listId={listId}
				userEmail={user.email}
				appliedFilters={query}
				onFilterChange={setQuery}
			/>
		</>
	);
};
